<template>
	<div class="notification-view" :class="extraClasses">
		<close-cross v-if="closeable" class="close-top-left-corner" @click.prevent="executeAction('seen')"></close-cross>
		<div class="nv-row" @click="openMainObject">
			<notification-icon :primary="notification.icon" :secondary="notification.secondaryIcon" :primary-color="notification.iconColor" :secondary-color="notification.secondaryIconColor"></notification-icon>
			<div class="nv-title-time">
				<div class="notification-title">{{ notification.title }}</div>
				<div class="nv-time-ref">
					<span v-if="hasOriginalDate" class="nv-time">{{ originalDate }}</span>
					<a v-if="notification.party" class="nv-ref" :href="'editParty.do?id='+notification.party" @click.prevent.stop="openParty">{{ notification.partyName || notification.party }}</a>
				</div>
			</div>
		</div>
		<div v-if="hasSender" class="notification-sender"><img :src="'avatar/'+notification.sender" class="mini-avatar"><span>{{ notification.senderName }}</span></div>
		<div v-if="hasBody" class="notification-body" @click="openMainObject">{{ notification.body }}</div>
		<div v-if="hasActions" class="notification-action-row">
			<select v-model="action" class="action notification-actions">
				<option value="-">{{ env.msg('bas.choices') }}</option>
				<option v-for="a in availableActions" :key="a.action" :value="a.action">{{ a.title }}</option>
			</select>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { Notification, NotificationAction, NotificationState } from "@t/meta/Notification"
import SymIcon from "@fd/helpers/SymIcon.vue";
import {computed, ref, watch, type CSSProperties} from "vue";
import {useRouter} from "vue-router";
import {injectEnv} from "@fd/Environment.ts";
import CloseCross from "@/fd/editors/file/CloseCross.vue";
import {parseDate, format} from "@t/util/DateUtil.ts";
import {SrcType} from "@t/meta/SourceType.ts";
import {isRoute} from "@/router/Router.ts";
import NotificationIcon from "@/notifications/NotificationIcon.vue";

const props = defineProps<{
	notification: Notification,
	closeable: boolean,
	panel: boolean
}>();
const emit = defineEmits(['action']);

const env = injectEnv();
const router = useRouter();
const action = ref<string>("-");

const hasOriginalDate = computed<boolean>(() => typeof props.notification.originalDate === 'string');
const hasBody = computed<boolean>(() => typeof props.notification.body === 'string' && props.notification.body.length > 0);
const hasSender = computed<boolean>(() => {
	return typeof props.notification.sender === 'number'
		&& typeof props.notification.senderName === 'string'
		&& props.notification.sender !== props.notification.user;	// only if differs from current user
});
const availableActions = computed<NotificationAction[]>(() => {
	if (!Array.isArray(props.notification.actions)) return [];
	if (props.panel) return props.notification.actions;	// panel mode (ringing) = all actions
	return props.notification.actions.filter((a) => !!a.keep);
});
const hasActions = computed<boolean>(() => availableActions.value.length > 0);
const extraClasses = computed<string[]>(() => {
	const c:string[] = [];
	if (props.closeable) c.push("closeable");
	if (props.panel) c.push("notification-view-panel");
	if (props.notification.color) c.push("color-" + props.notification.color);
	return c;
});
const originalDate = computed<string>(() => {
	const parsed = parseDate(props.notification.originalDate);
	return format(parsed, env);
});

function openParty(event:MouseEvent) {
	env.navigateTo(SrcType.PARTY, props.notification.party);
}

function openMainObject(event:MouseEvent) {
	event.preventDefault();
	if (props.notification.link) {
		if (isRoute(props.notification.link)) {
			router.push(props.notification.link);
		} else {
			window.location.href = props.notification.link;
		}
	} else {
		if (props.notification.srcType && props.notification.srcId) {
			env.navigateTo(props.notification.srcType, props.notification.srcId);
		}
	}
}

function executeAction(name:string):void {
	console.log("executeAction " + name);
	action.value = "-";
	emit('action', name, [ props.notification.id ]);
}

watch(() => action.value, (a) => {
	console.log("NotificationView.watch action.value=" + action.value + " a=" + a);
	if ("-" !== a) executeAction(a);
});
</script>

<style>

.notification-view {
	--nt-width: 284px;
	padding: 16px 8px;
	width: 300px;
	position: relative;

	display: inline-flex;
	flex-direction: column;
	row-gap: 8px;
	background-color: var(--bg);
	color: var(--fg);
	cursor: pointer;
	box-sizing: border-box;
	overflow-clip-margin: 8px;
}
.notification-view-panel {
	--nt-width: 268px;
	padding: 15px;	/* 15 padding + 1px border = 16px */
	border: 1px solid var(--border-color);
	box-shadow: var(--panel-shadow);
	border-radius: var(--menu-radius);
	pointer-events: auto;
}
.nv-row {
	display: flex;
	flex-direction: row;
	column-gap: 8px;
}
.nv-title-time {

}
.nv-time-ref {
	font-size: var(--font-size-s);
	line-height: 16px;
	display: flex;
	flex-direction: row;
	column-gap: 8px;
	align-items: baseline;
	justify-content: space-between;
	width: calc(var(--nt-width) - 32px - 8px);
}
.nv-time {
	opacity: 0.75;
	white-space: nowrap;
	overflow:hidden;
	text-overflow: ellipsis;
}
.nv-ref {
	white-space: nowrap;
	overflow:hidden;
	text-overflow: ellipsis;
	text-align: right;
}

.notification-title {
	font-weight: bold;
	line-height: 16px;
	height: 16px;
	width: calc(var(--nt-width) - 32px - 8px);
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	box-sizing: border-box;
}
.notification-body {
	font-size: 12px;
	line-height: 16px;
	opacity: 0.75;
	max-height: 48px;
	overflow: hidden;
	text-overflow: ellipsis;
	box-sizing: border-box;
}
.notification-action-row {
	text-align: right;
}
.notification-actions {
	font-size: var(--font-size-s);
}

.notification-sender {
	font-size: var(--font-size-s);
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	column-gap: 4px;
	padding-left: 38px;
}
</style>